// frontend/src/pages/GenerationsByStyle.jsx
// ✅ FINAL: Cleaner layout with controls and nav buttons in separate rows

import React from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import Screen from "../uizard/ui/Screen";
import { getIdentityHeaders, waitForIdentity } from '../lib/identityGoogle';
import { 
  API_BASE, 
  normalizeListResponse,
  getImageUrl,
  fromNow,
  shorten,
  toast 
} from "../lib/api";

const api = (path) => API_BASE ? `${API_BASE}${path}` : path;

/** Brand tokens (match directory + profile look) */
const brand = {
  purple: "#6D5EF9",
  purpleDark: "#5B4EE8",
  purpleTint: "#F3F0FF",
  purpleBorder: "#E7E2FF",
  neutralText: "#111827",
  neutralBorder: "#E5E7EB",
  shadow: "0 10px 24px rgba(17,24,39,.08)",
};

/* ------------------------------ Helpers ------------------------------ */
function normalizeGen(d) {
  return {
    id: d.id || d._id || "",
    prompt: d.prompt || "",
    style: d.style || "default",
    aspectRatio: d.aspectRatio || "1:1",
    status: d.status || "completed",
    imageUrl: getImageUrl(d),
    createdAt: d.createdAt || Date.now(),
    allowOnHome: !!d.allowOnHome,
    uid: d.uid || "",
    username: d.username || "",
  };
}

/* ------------------------------ Page ------------------------------ */
export default function GenerationsByStyle() {
  const { styleId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightGenId = searchParams.get('generation');
  const reportId = searchParams.get('report');

  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState("");
  const [items, setItems] = React.useState([]);
  const [styleMeta, setStyleMeta] = React.useState(null);
  const [sortBy, setSortBy] = React.useState("newest");
  const [resolving, setResolving] = React.useState(false);
  
  // ✅ FIXED: Auto-switch to "reported" view when report parameter is present
  const [view, setView] = React.useState(() => {
    const urlView = searchParams.get('view');
    const hasReport = searchParams.get('report');
    
    // If there's a report ID but no explicit view, default to "reported"
    if (hasReport && !urlView) {
      return "reported";
    }
    
    return urlView || "all";
  });

  React.useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);
        setErr("");
        await waitForIdentity();

        console.log(`[GenerationsByStyle] Loading generations for style: ${styleId}, view: ${view}`);

        // ✅ UPDATED: Include view parameter
        const res = await fetch(
          api(`/api/creator/styles/${encodeURIComponent(styleId)}/all-generations?view=${view}`),
          { 
            credentials: "include",
            headers: { ...getIdentityHeaders() }
          }
        );

        if (!res.ok) {
          const errorText = await res.text();
          console.error(`[GenerationsByStyle] API error ${res.status}:`, errorText);
          
          if (res.status === 404) {
            throw new Error("Style not found");
          } else if (res.status === 403) {
            throw new Error("You don't own this style");
          } else {
            throw new Error(`Failed to load generations: ${res.status}`);
          }
        }

        const data = await res.json();
        const allGenerations = Array.isArray(data.items) ? data.items : [];
        
        console.log(`[GenerationsByStyle] Loaded ${allGenerations.length} generations (view: ${view})`);

        if (alive) {
          setItems(allGenerations.map(normalizeGen));
        }

        // Optional: fetch style meta
        try {
          const m = await fetch(
            api(`/api/creator/styles/${encodeURIComponent(styleId)}`),
            {
              credentials: "include",
              headers: { ...getIdentityHeaders() },
            }
          );

          if (m.ok) {
            const hit = await m.json();
            if (alive && hit) {
              setStyleMeta({
                id: hit.id,
                name: hit.name || "Untitled",
                status: hit.status || "trained",
              });
            }
          }
        } catch {
          // style meta is optional
        }

      } catch (e) {
        console.error("[GenerationsByStyle] Error:", e);
        if (alive) setErr(e.message || "Failed to load generations");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => { alive = false; };
  }, [styleId, view]); // ✅ Re-fetch when view changes

  // ✅ Update URL when view changes
  React.useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    if (view !== "all") {
      newParams.set('view', view);
    } else {
      newParams.delete('view');
    }
    
    // Preserve other params (generation, report)
    const newUrl = newParams.toString() ? `?${newParams}` : '';
    if (window.location.search !== newUrl) {
      setSearchParams(newParams, { replace: true });
    }
  }, [view]);

  // Scroll to and highlight specific generation
  React.useEffect(() => {
    if (!highlightGenId) return;
    if (loading || items.length === 0) return;
    
    const genExists = items.some(item => item && item.id === highlightGenId);
    if (!genExists) {
      console.warn('[Highlight] Generation not found in list:', highlightGenId);
      toast("⚠️ Generation not found. It may have been deleted.");
      
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('generation');
      setSearchParams(newParams, { replace: true });
      
      return;
    }
    
    const timer = setTimeout(() => {
      try {
        const element = document.getElementById(`gen-${highlightGenId}`);
        if (element) {
          console.log('[Highlight] ✅ Scrolling to generation:', highlightGenId);
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.classList.add('highlighted');
        }
      } catch (err) {
        console.error('[Highlight] Error:', err);
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [highlightGenId, items, loading, searchParams, setSearchParams]);

  function copyStyleId() {
    navigator.clipboard.writeText(String(styleId))
      .then(() => toast("Style ID copied"))
      .catch(() => toast("Couldn't copy"));
  }

  async function resolveReport(action) {
    if (!reportId) return;
    if (resolving) return;

    const confirmMsg = action === "refund"
      ? "Refund 1 credit to this user and mark as resolved?"
      : "Dismiss this report as invalid?";

    if (!window.confirm(confirmMsg)) return;

    setResolving(true);

    try {
      await waitForIdentity();

      const res = await fetch(
        api(`/api/feedback/my-styles/reports/${encodeURIComponent(reportId)}/resolve`),
        {
          method: "PATCH",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            ...getIdentityHeaders(),
          },
          body: JSON.stringify({ action }),
        }
      );

      const contentType = res.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        throw new Error("Server returned non-JSON response");
      }

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || `Server error: ${res.status}`);
      }
      
      toast(data.message || "Report resolved");
      
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('report');
      newParams.delete('generation');
      setSearchParams(newParams, { replace: true });
      
      // Reload data
      window.location.reload();
      
    } catch (e) {
      console.error("[Resolve] Error:", e);
      toast(e.message || "Failed to resolve report");
    } finally {
      setResolving(false);
    }
  }

  // Sort items
  const sortedItems = React.useMemo(() => {
    const sorted = [...items];
    switch (sortBy) {
      case "oldest":
        return sorted.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      case "newest":
      default:
        return sorted.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }
  }, [items, sortBy]);

  return (
    <Screen>
      <StyleTag />

      <section className="gs-hero">
        <div className="gs-hero__text">
          <h1 
            className="h2"
            style={{
              background: "linear-gradient(135deg, #1E3A8A 0%, #6D28D9 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {view === "reported" ? "Reported Generations" : "All Generations"} — Style
          </h1>
          <div className="muted">
            ID: <code>{shorten(String(styleId))}</code>
            <button className="link" onClick={copyStyleId} style={{ marginLeft: 8 }}>Copy</button>
            {items.length > 0 && (
              <span style={{ marginLeft: 8 }}>
                • {items.length} generation{items.length !== 1 ? 's' : ''} {view === "reported" ? "(reported)" : ""}
              </span>
            )}
          </div>
          {styleMeta ? (
            <div className="gs-sub">
              <strong className="gs-name">{styleMeta.name}</strong>
              <span className={`badge ${mapStatus(styleMeta.status).cls}`} style={{ marginLeft: 8 }}>
                {mapStatus(styleMeta.status).text}
              </span>
            </div>
          ) : null}
        </div>

        {/* ✅ First row: View toggle + Sort */}
        <div className="gs-controls-row">
          <div className="view-toggle">
            <button
              className={`toggle-btn ${view === "all" ? "active" : ""}`}
              onClick={() => setView("all")}
            >
              All Usage
            </button>
            <button
              className={`toggle-btn ${view === "reported" ? "active" : ""}`}
              onClick={() => setView("reported")}
            >
              Reported Only
            </button>
          </div>

          <div className="sort-wrapper">
            <label htmlFor="sort-select" className="sort-label">Sort by:</label>
            <select 
              id="sort-select"
              className="sort-select" 
              value={sortBy} 
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
            </select>
          </div>
        </div>

        {/* ✅ Second row: Navigation buttons */}
        <div className="gs-nav-row">
          <Link to="/manage-styles" className="btn">← Manage Styles</Link>
          <Link to="/gallery" className="btn">Back to Gallery</Link>
        </div>
      </section>

      <section>
        {loading ? (
          <SkeletonGrid />
        ) : err ? (
          <div className="gs-error">{err}</div>
        ) : !items.length ? (
          <div className="gs-empty">
            {view === "reported" 
              ? "No reported generations for this style. This is good news!"
              : "No generations match this style yet."}
          </div>
        ) : (
          <div className="gs-grid">
            {sortedItems.map(it => {
              const isHighlighted = highlightGenId === it.id;
              
              return (
                <figure 
                  key={it.id} 
                  id={`gen-${it.id}`} 
                  className={`gs-card ${isHighlighted ? 'highlighted' : ''}`}
                  title={it.prompt || ""}
                >
                  <img src={it.imageUrl} alt={it.prompt || "generation"} loading="lazy" />
                  <figcaption>
                    <div className="cap-top">
                      <span className="cap-user">{it.uid ? shorten(it.uid) : (it.username || "")}</span>
                      <span className="cap-time">{fromNow(it.createdAt)}</span>
                    </div>
                    {it.prompt ? <div className="cap-prompt">{it.prompt}</div> : null}
                    <div className="cap-gen-id">ID: {shorten(it.id)}</div>
                    
                    {isHighlighted && reportId && (
                      <div className="report-actions-inline">
                        <button
                          className="action-btn-inline action-refund"
                          onClick={() => resolveReport("refund")}
                          disabled={resolving}
                        >
                          {resolving ? "Processing..." : "💰 Refund & Resolve"}
                        </button>
                        <button
                          className="action-btn-inline action-dismiss"
                          onClick={() => resolveReport("dismiss")}
                          disabled={resolving}
                        >
                          {resolving ? "Processing..." : "✖ Dismiss"}
                        </button>
                      </div>
                    )}
                  </figcaption>
                </figure>
              );
            })}
          </div>
        )}
      </section>
    </Screen>
  );
}

/* ------------------------------ UI bits ------------------------------ */
function SkeletonGrid() {
  return (
    <div className="gs-grid">
      {Array.from({ length: 12 }).map((_, i) => (
        <div key={i} className="sk-card" />
      ))}
    </div>
  );
}

function mapStatus(status = "") {
  const s = String(status).toLowerCase();
  const map = {
    trained: { text: "Live", cls: "ok" },
    training: { text: "Training", cls: "warn" },
    queued: { text: "Queued", cls: "muted" },
    failed: { text: "Failed", cls: "err" },
    rejected: { text: "Rejected", cls: "err" },
  };
  return map[s] || { text: s || "—", cls: "muted" };
}

/* ------------------------------- Styles ------------------------------- */
function StyleTag() {
  return (
    <style>{`
.gs-hero{
  display:flex;
  flex-direction:column;
  gap:16px;
  border:1px solid ${brand.purpleBorder};
  border-radius:16px;
  padding:16px;
  margin-bottom:16px;
  background: linear-gradient(180deg, ${brand.purpleTint}, #fff);
}

.gs-hero__text{
  flex:1;
}

.gs-controls-row{
  display:flex;
  align-items:center;
  gap:12px;
  flex-wrap:wrap;
}

.gs-nav-row{
  display:flex;
  gap:10px;
  flex-wrap:wrap;
}

.gs-sub{
  margin-top:6px;
  display:flex;
  align-items:center;
}

.gs-name{
  font-weight:800;
  color:${brand.neutralText};
}

.view-toggle{
  display:flex;
  border-radius:12px;
  overflow:hidden;
  border:1px solid ${brand.neutralBorder};
  background:#fff;
}

.toggle-btn{
  height:42px;
  padding:0 16px;
  border:none;
  background:#fff;
  color:${brand.neutralText};
  font-weight:700;
  font-size:14px;
  cursor:pointer;
  transition: all 0.2s;
  border-right:1px solid ${brand.neutralBorder};
}

.toggle-btn:last-child{
  border-right:none;
}

.toggle-btn.active{
  background:${brand.purple};
  color:#fff;
}

.toggle-btn:not(.active):hover{
  background:${brand.purpleTint};
}

.sort-wrapper{
  display:flex;
  align-items:center;
  gap:8px;
}

.sort-label{
  font-weight:700;
  color:${brand.neutralText};
  font-size:14px;
}

.sort-select{
  height:42px;
  padding:0 36px 0 14px;
  border-radius:12px;
  border:1px solid ${brand.neutralBorder};
  background:#fff url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="12" height="8" viewBox="0 0 12 8"><path fill="%23374151" d="M1.41 0L6 4.59 10.59 0 12 1.41l-6 6-6-6z"/></svg>') no-repeat right 12px center;
  font-weight:700;
  font-size:14px;
  cursor:pointer;
  appearance:none;
}

.sort-select:focus{
  border-color:${brand.purple};
  outline:none;
  box-shadow:0 0 0 3px ${brand.purpleTint};
}

.btn{
  height:42px;
  padding:0 16px;
  border-radius:12px;
  border:1px solid ${brand.neutralBorder};
  background:#fff;
  font-weight:700;
  text-decoration:none;
  display:inline-flex;
  align-items:center;
  transition: all 0.2s;
  color:${brand.neutralText};
}

.btn:hover{
  background:${brand.purpleTint};
  border-color:${brand.purple};
}

.link{
  color:${brand.purple};
  font-weight:700;
  background:none;
  border:none;
  padding:0;
  cursor:pointer;
}

.badge{
  border-radius:999px;
  padding:6px 10px;
  font-size:12px;
  font-weight:900;
  border:1px solid #ECEEF2;
  background:#fff;
  color:#374151;
}

.badge.ok{
  background:#E8F7F0;
  color:#096645;
  border-color:#CDECDD;
}

.badge.warn{
  background:#FFF7E8;
  color:#8a5a00;
  border-color:#F5E6C3;
}

.badge.err{
  background:#FFE9E9;
  color:#8a1f1f;
  border-color:#F2C2C2;
}

.badge.muted{
  background:#F3F4F6;
  color:#4B5563;
  border-color:#E5E7EB;
}

/* Mobile adjustments */
@media (max-width: 640px){
  .gs-controls-row{
    flex-direction:column;
    width:100%;
  }
  
  .view-toggle{
    width:100%;
  }
  
  .toggle-btn{
    flex:1;
    height:38px;
    font-size:13px;
  }
  
  .sort-wrapper{
    width:100%;
    justify-content:space-between;
  }
  
  .sort-select{
    flex:1;
    height:38px;
    font-size:13px;
  }
  
  .gs-nav-row{
    flex-direction:column;
    width:100%;
  }
  
  .btn{
    width:100%;
    height:38px;
    justify-content:center;
    font-size:14px;
  }
}

.gs-grid{
  display:grid;
  gap:16px;
  grid-template-columns: 1fr;
}

@media (min-width: 640px){
  .gs-grid{
    grid-template-columns: repeat(2, minmax(0,1fr));
  }
}

@media (min-width: 880px){
  .gs-grid{
    grid-template-columns: repeat(3, minmax(0,1fr));
  }
}

@media (min-width: 1160px){
  .gs-grid{
    grid-template-columns: repeat(4, minmax(0,1fr));
  }
}

.gs-card{
  border:1px solid ${brand.neutralBorder};
  border-radius:14px;
  overflow:hidden;
  background:#fff;
  box-shadow:${brand.shadow};
  transition: all 0.3s ease;
}

.gs-card img{
  width:100%;
  display:block;
  object-fit:contain;
  background:#000;
  max-height:600px;
}

.gs-card figcaption{
  padding:10px;
  display:grid;
  gap:6px;
}

.cap-top{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:8px;
}

.cap-user{
  font-weight:800;
  color:${brand.neutralText};
}

.cap-time{
  color:#6B7280;
  font-size:12px;
}

.cap-prompt{
  color:#374151;
  font-size:14px;
  line-height:1.35;
}

.cap-gen-id{
  font-family:monospace;
  font-size:11px;
  color:#9CA3AF;
  background:#F9FAFB;
  padding:3px 6px;
  border-radius:4px;
  display:inline-block;
}

.report-actions-inline{
  display:flex;
  gap:8px;
  margin-top:12px;
  padding-top:12px;
  border-top:1px solid ${brand.neutralBorder};
}

.action-btn-inline{
  flex:1;
  height:36px;
  padding:0 12px;
  border-radius:8px;
  border:none;
  font-size:13px;
  font-weight:700;
  cursor:pointer;
  transition: all 0.2s;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap:6px;
}

.action-btn-inline:disabled{
  opacity:0.6;
  cursor:not-allowed;
}

.action-btn-inline.action-refund{
  background:#10B981;
  color:#fff;
}

.action-btn-inline.action-refund:hover:not(:disabled){
  background:#059669;
  transform:translateY(-1px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.action-btn-inline.action-dismiss{
  background:#6B7280;
  color:#fff;
}

.action-btn-inline.action-dismiss:hover:not(:disabled){
  background:#4B5563;
  transform:translateY(-1px);
  box-shadow: 0 4px 12px rgba(107, 114, 128, 0.3);
}

.gs-card.highlighted{
  animation: highlight-pulse 3s ease-in-out infinite;
  border: 3px solid ${brand.purple} !important;
  box-shadow: 0 0 0 6px ${brand.purpleTint}, 0 8px 24px rgba(109, 94, 249, 0.4) !important;
  position: relative;
  z-index: 10;
}

.gs-card.highlighted figcaption{
  background: linear-gradient(135deg, ${brand.purpleTint} 0%, #fff 100%);
}

.gs-card.highlighted figcaption::before{
  content: "📍 Reported Generation";
  display: block;
  background: ${brand.purple};
  color: white;
  padding: 6px 10px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 800;
  text-align: center;
  margin-bottom: 8px;
}

@keyframes highlight-pulse{
  0%, 100%{
    box-shadow: 0 0 0 6px ${brand.purpleTint}, 0 8px 24px rgba(109, 94, 249, 0.6);
  }
  50%{
    box-shadow: 0 0 0 8px ${brand.purpleTint}, 0 12px 32px rgba(109, 94, 249, 0.8);
  }
}

@media (max-width: 640px){
  .gs-card figcaption{
    padding:8px;
  }
  
  .cap-user{
    display:none;
  }
  
  .cap-prompt{
    font-size:13px;
    line-height:1.3;
    display:-webkit-box;
    -webkit-line-clamp:2;
    -webkit-box-orient:vertical;
    overflow:hidden;
  }
  
  .cap-gen-id{
    font-size:10px;
    margin-top:4px;
  }
}

.gs-empty{
  padding:14px;
  border:1px dashed ${brand.neutralBorder};
  border-radius:12px;
  color:#6B7280;
}

.gs-error{
  margin-top:14px;
  color:#B91C1C;
  background:#FFF7F7;
  border:1px solid #F2C2C2;
  border-radius:12px;
  padding:12px;
}

.sk-card{
  height:260px;
  border-radius:14px;
  border:1px solid ${brand.neutralBorder};
  background: linear-gradient(90deg,#f5f5f7,#ececf2,#f5f5f7);
  animation: sk 1.2s infinite;
}

@keyframes sk{
  0%{
    background-position:-200px 0
  }
  100%{
    background-position:calc(200px + 100%) 0
  }
}
    `}</style>
  );
}